import { useState } from 'react';
import { useEditMode } from '@/contexts/EditModeContext';
import { useAuth } from '@/contexts/AuthContext';
import { Pencil, Check, X, Plus, Trash2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface QAItem {
  question: string;
  answer: string;
}

interface EditableQAProps {
  items: QAItem[];
  onSave: (items: QAItem[]) => Promise<void>;
  onGenerate?: () => void;
  isGenerating?: boolean;
  className?: string;
}

export const EditableQA = ({ items, onSave, onGenerate, isGenerating = false, className }: EditableQAProps) => {
  const { isEditMode } = useEditMode();
  const { isEditor } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editItems, setEditItems] = useState<QAItem[]>(items);
  const [isSaving, setIsSaving] = useState(false);

  if (!isEditor || !isEditMode) {
    if (items.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          No Q&A items yet.
        </div>
      );
    }

    return (
      <Accordion type="single" collapsible className={cn('w-full', className)}>
        {items.map((item, index) => (
          <AccordionItem key={index} value={`item-${index}`}>
            <AccordionTrigger>{item.question}</AccordionTrigger>
            <AccordionContent>{item.answer}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    );
  }

  const handleSave = async () => {
    const filteredItems = editItems.filter(
      (item) => item.question.trim() !== '' && item.answer.trim() !== ''
    );
    setIsSaving(true);
    try {
      await onSave(filteredItems);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditItems(items);
    setIsEditing(false);
  };

  const handleAdd = () => {
    setEditItems([...editItems, { question: '', answer: '' }]);
  };

  const handleRemove = (index: number) => {
    setEditItems(editItems.filter((_, i) => i !== index));
  };

  const handleUpdateQuestion = (index: number, value: string) => {
    const newItems = [...editItems];
    newItems[index].question = value;
    setEditItems(newItems);
  };

  const handleUpdateAnswer = (index: number, value: string) => {
    const newItems = [...editItems];
    newItems[index].answer = value;
    setEditItems(newItems);
  };

  if (isEditing) {
    return (
      <div className="space-y-4">
        {editItems.map((item, index) => (
          <Card key={index} className="p-4">
            <div className="space-y-3">
              <div className="flex items-start justify-between">
                <Label className="text-sm font-semibold">Q&A {index + 1}</Label>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleRemove(index)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
              <div className="space-y-2">
                <Label>Question</Label>
                <Input
                  value={item.question}
                  onChange={(e) => handleUpdateQuestion(index, e.target.value)}
                  placeholder="Enter question..."
                />
              </div>
              <div className="space-y-2">
                <Label>Answer</Label>
                <Textarea
                  value={item.answer}
                  onChange={(e) => handleUpdateAnswer(index, e.target.value)}
                  placeholder="Enter answer..."
                  className="min-h-[100px]"
                />
              </div>
            </div>
          </Card>
        ))}
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleAdd} className="flex-1">
            <Plus className="h-4 w-4 mr-1" />
            Add Q&A
          </Button>
          {onGenerate && (
            <Button 
              variant="outline" 
              onClick={() => {
                onGenerate();
                setIsEditing(false);
              }} 
              disabled={isGenerating}
              className="flex-1 bg-gradient-to-r from-purple-500/10 to-pink-500/10 hover:from-purple-500/20 hover:to-pink-500/20 border-purple-300"
            >
              <Sparkles className="h-4 w-4 mr-1" />
              {isGenerating ? 'Generating...' : 'Generate with AI'}
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={isSaving}>
            <Check className="h-4 w-4 mr-1" />
            Save All
          </Button>
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('group relative', className)}>
      <div className="border-2 border-dashed border-transparent hover:border-orange-300 hover:bg-orange-50/10 transition-all rounded p-4">
        {items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No Q&A items yet. Click to add some.
          </div>
        ) : (
          <Accordion type="single" collapsible className="w-full">
            {items.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger>{item.question}</AccordionTrigger>
                <AccordionContent>{item.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
      <Button
        size="sm"
        variant="ghost"
        className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-orange-500 hover:bg-orange-600 text-white"
        onClick={() => setIsEditing(true)}
      >
        <Pencil className="h-3 w-3 mr-1" />
        Edit Q&A
      </Button>
    </div>
  );
};
